import { Card } from '@/components/ui/card';
import { 
    Calendar, 
    Phone, 
    PhoneCall,
    Users,
    Bot,
    ClipboardCheck,
    Tag,
    Target,
    CheckCircle2
} from 'lucide-react';
import type { Rubric, CustomFieldPayloadItem } from '@/api/rubrics/types';
import { motion, type Variants } from 'framer-motion';
import { cn } from '@/lib/utils';
import { FadeId } from '@/components/ui/fade-id';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

interface RubricDisplayProps {
    rubric: Rubric;
    onEdit: () => void;
    onDelete?: () => void;
    variants?: Variants;
}

interface StatButtonProps {
    icon: React.ElementType;
    value: string | number;
    label: string;
    tooltip: string;
    active?: boolean;
}

const StatButton = ({ icon: Icon, value, label, tooltip, active = false }: StatButtonProps) => {
    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        className={cn(
                            "h-11 flex flex-col items-center justify-center gap-0 px-2.5 py-2",
                            "hover:bg-muted/50 transition-colors cursor-default",
                            active && "bg-primary/5"
                        )}
                    >
                        <div className="flex items-center gap-1 mb-0.5">
                            <Icon className="h-3.5 w-3.5 text-primary" />
                            <span className="text-xs font-semibold">{value}</span>
                        </div>
                        <span className="text-[9px] text-muted-foreground uppercase tracking-wide leading-tight">{label}</span>
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    <p className="text-xs">{tooltip}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

const RubricDisplay = ({ rubric, onEdit, variants }: RubricDisplayProps) => {
    // Helper function to get custom fields count
    const getCustomFieldsCount = () => {
        const excludedFields = [
            'name', 'type', 'organizationId', 'isActive', 'createdAt', 'updatedAt',
            'assistantIds', 'phoneNumberIds', 'multiAgentIds', 'successEvaluation', '_id',
            'createdBy', 'updatedBy', '__v'
        ];

        return Object.entries(rubric).filter(([key, value]) => 
            !excludedFields.includes(key) &&
            Array.isArray(value) &&
            value.length > 0 &&
            value[0] !== null &&
            typeof value[0] === 'object' &&
            'key' in value[0] &&
            'value' in value[0] &&
            'dataType' in value[0]
        ).length;
    };

    // Calculate stats
    const customFieldsCount = getCustomFieldsCount();
    const assistantsCount = rubric.assistantIds?.length || 0;
    const phoneNumbersCount = rubric.phoneNumberIds?.length || 0;
    const multiAgentsCount = rubric.multiAgentIds?.length || 0;
    const hasSuccessEval = rubric.successEvaluation ? 'Yes' : 'No';
    
    // Calculate total connections
    const totalConnections = assistantsCount + phoneNumbersCount + multiAgentsCount;

    return (
        <motion.div variants={variants}>
            <Card
                className="group relative overflow-hidden transition-all duration-200 hover:shadow-md cursor-pointer"
                onClick={onEdit}
            >
                {/* Subtle gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                {/* Main Content */}
                <div className="relative p-5 space-y-4">
                    {/* Header with Icon, Title, and ID */}
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            {/* Icon */}
                            <div className="relative flex-shrink-0">
                                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 flex items-center justify-center ring-1 ring-primary/10 shadow-sm">
                                    <ClipboardCheck className="h-5 w-5 text-primary" />
                                </div>
                            </div>

                            {/* Title and Date */}
                            <div className="min-w-0 flex-1">
                                <h3 className="text-base font-semibold tracking-tight truncate">
                                    {rubric.name}
                                </h3>
                                {/* Date */}
                                {rubric.updatedAt && (
                                    <div className="flex items-center gap-1.5 mt-1">
                                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                                        <span className="text-xs text-muted-foreground">
                                            {new Date(rubric.updatedAt).toLocaleDateString()}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ID on Right */}
                        <div onClick={(e) => e.stopPropagation()}>
                            <FadeId 
                                id={rubric._id} 
                                label="Rubric ID"
                                tooltipSide="top"
                            />
                        </div>
                    </div>

                    {/* Unified Configuration Grid with Dividers */}
                    <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
                        <div className="grid grid-cols-3 divide-x divide-border">
                            {/* Primary Config Row - Type, Custom Fields, Success Evaluation */}
                            <StatButton
                                icon={PhoneCall}
                                value={rubric.type === 'inboundPhoneCall' ? 'Inbound' : 'Outbound'}
                                label="Type"
                                tooltip={rubric.type === 'inboundPhoneCall' ? 'Inbound Phone Call' : 'Outbound Phone Call'}
                            />
                            
                            <StatButton
                                icon={Tag}
                                value={customFieldsCount}
                                label="Fields"
                                tooltip={`${customFieldsCount} custom field${customFieldsCount === 1 ? '' : 's'}`}
                                active={customFieldsCount > 0}
                            />

                            <StatButton
                                icon={Target}
                                value={hasSuccessEval}
                                label="Evaluation"
                                tooltip={hasSuccessEval === 'Yes' ? 'Success evaluation criteria defined' : 'No success evaluation criteria'}
                                active={hasSuccessEval === 'Yes'}
                            />
                        </div>
                        
                        {/* Horizontal Divider */}
                        <div className="border-t border-border" />
                        
                        <div className="grid grid-cols-3 divide-x divide-border">
                            {/* Secondary Config Row - Assistants, Phone Numbers, Multi-Agents */}
                            <StatButton
                                icon={Bot}
                                value={assistantsCount}
                                label="Assistants"
                                tooltip={`${assistantsCount} assistant${assistantsCount === 1 ? '' : 's'} linked`}
                                active={assistantsCount > 0}
                            />

                            <StatButton
                                icon={Phone}
                                value={phoneNumbersCount}
                                label="Phones"
                                tooltip={`${phoneNumbersCount} phone number${phoneNumbersCount === 1 ? '' : 's'} linked`}
                                active={phoneNumbersCount > 0}
                            />

                            <StatButton
                                icon={Users}
                                value={multiAgentsCount}
                                label="Multi-Agent"
                                tooltip={`${multiAgentsCount} multi-agent${multiAgentsCount === 1 ? '' : 's'} linked`}
                                active={multiAgentsCount > 0}
                            />
                        </div>
                    </div>
                </div>
            </Card>
        </motion.div>
    );
};

export default RubricDisplay;
